'use client';


import { c } from "react-compiler-runtime";
import * as React$1 from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import * as ReactDOM from "react-dom";

//#region src/components/Textarea/TextareaAutosize.tsx
function debounce(func, wait = 166) {
	let timeout;
	function debounced(...args) {
		const later = () => {
			func.apply(this, args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	}
	debounced.clear = () => {
		clearTimeout(timeout);
	};
	return debounced;
}
const useEnhancedEffect = typeof window !== "undefined" ? React$1.useLayoutEffect : React$1.useEffect;
function ownerDocument(node) {
	return node?.ownerDocument || document;
}
function ownerWindow(node) {
	const doc = ownerDocument(node);
	return doc.defaultView || window;
}
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
function useForkRef(...t0) {
	const $ = c(2);
	const refs = t0;
	let t1;
	bb0: {
		if (refs.every(_temp)) {
			t1 = null;
			break bb0;
		}
		let t2;
		if ($[0] !== refs) {
			t2 = (instance) => {
				refs.forEach((ref_0) => {
					setRef(ref_0, instance);
				});
			};
			$[0] = refs;
			$[1] = t2;
		} else t2 = $[1];
		t1 = t2;
	}
	return t1;
}
function _temp(ref) {
	return ref == null;
}
function getStyleValue(value) {
	return Number.parseInt(value, 10) || 0;
}
const styles = { shadow: {
	visibility: "hidden",
	position: "absolute",
	overflow: "hidden",
	height: 0,
	top: 0,
	left: 0,
	transform: "translateZ(0)"
} };
function isEmpty(obj) {
	return obj === void 0 || obj === null || Object.keys(obj).length === 0 || obj.outerHeightStyle === 0 && !obj.overflow;
}
/**
*
* Demos:
*
* - [Textarea Autosize](https://mui.com/base/react-textarea-autosize/)
* - [Textarea Autosize](https://mui.com/material-ui/react-textarea-autosize/)
*
* API:
*
* - [TextareaAutosize API](https://mui.com/base/api/textarea-autosize/)
*/
const TextareaAutosize = React$1.forwardRef(function TextareaAutosize$1(props, ref) {
	const $ = c(46);
	let maxRows;
	let onChange;
	let other;
	let style;
	let t0;
	let value;
	if ($[0] !== props) {
		({onChange, maxRows, minRows: t0, style, value, ...other} = props);
		$[0] = props;
		$[1] = maxRows;
		$[2] = onChange;
		$[3] = other;
		$[4] = style;
		$[5] = t0;
		$[6] = value;
	} else {
		maxRows = $[1];
		onChange = $[2];
		other = $[3];
		style = $[4];
		t0 = $[5];
		value = $[6];
	}
	const minRows = t0 === void 0 ? 1 : t0;
	const { current: isControlled } = React$1.useRef(value != null);
	const inputRef = React$1.useRef(null);
	const handleRef = useForkRef(ref, inputRef);
	const shadowRef = React$1.useRef(null);
	const renders = React$1.useRef(0);
	let t1;
	if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
		t1 = { outerHeightStyle: 0 };
		$[7] = t1;
	} else t1 = $[7];
	const [state, setState] = React$1.useState(t1);
	let t2;
	if ($[8] !== maxRows || $[9] !== minRows || $[10] !== props.placeholder) {
		t2 = () => {
			const input = inputRef.current;
			const containerWindow = ownerWindow(input);
			const computedStyle = containerWindow.getComputedStyle(input);
			if (computedStyle.width === "0px") return { outerHeightStyle: 0 };
			const inputShallow = shadowRef.current;
			inputShallow.style.width = computedStyle.width;
			inputShallow.value = input.value || props.placeholder || "x";
			if (inputShallow.value.slice(-1) === "\n") inputShallow.value = inputShallow.value + " ";
			const boxSizing = computedStyle.boxSizing;
			const padding = getStyleValue(computedStyle.paddingBottom) + getStyleValue(computedStyle.paddingTop);
			const border = getStyleValue(computedStyle.borderBottomWidth) + getStyleValue(computedStyle.borderTopWidth);
			const innerHeight = inputShallow.scrollHeight;
			inputShallow.value = "x";
			const singleRowHeight = inputShallow.scrollHeight;
			let outerHeight = innerHeight;
			if (minRows) outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
			if (maxRows) outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
			outerHeight = Math.max(outerHeight, singleRowHeight);
			const outerHeightStyle = outerHeight + (boxSizing === "border-box" ? padding + border : 0);
			const overflow = Math.abs(outerHeight - innerHeight) <= 1;
			return {
				outerHeightStyle,
				overflow
			};
		};
		$[8] = maxRows;
		$[9] = minRows;
		$[10] = props.placeholder;
		$[11] = t2;
	} else t2 = $[11];
	const getUpdatedState = t2;
	let t3;
	if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
		t3 = (prevState, newState) => {
			const { outerHeightStyle: outerHeightStyle_0, overflow: overflow_0 } = newState;
			if (renders.current < 20 && (outerHeightStyle_0 > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle_0) > 1 || prevState.overflow !== overflow_0)) {
				renders.current = renders.current + 1;
				return {
					overflow: overflow_0,
					outerHeightStyle: outerHeightStyle_0
				};
			}
			if (renders.current === 20) console.error(["MUI: Too many re-renders. The layout is unstable.", "TextareaAutosize limits the number of renders to prevent an infinite loop."].join("\n"));
			return prevState;
		};
		$[12] = t3;
	} else t3 = $[12];
	const updateState = t3;
	let t4;
	if ($[13] !== getUpdatedState) {
		t4 = () => {
			const newState_0 = getUpdatedState();
			if (isEmpty(newState_0)) return;
			setState((prevState_0) => updateState(prevState_0, newState_0));
		};
		$[13] = getUpdatedState;
		$[14] = t4;
	} else t4 = $[14];
	const syncHeight = t4;
	let t5;
	if ($[15] !== getUpdatedState) {
		t5 = () => {
			const newState_1 = getUpdatedState();
			if (isEmpty(newState_1)) return;
			ReactDOM.flushSync(() => {
				setState((prevState_1) => updateState(prevState_1, newState_1));
			});
		};
		$[15] = getUpdatedState;
		$[16] = t5;
	} else t5 = $[16];
	const syncHeightWithFlushSycn = t5;
	let t6;
	if ($[17] !== syncHeightWithFlushSycn) {
		t6 = () => {
			const handleResize = debounce(() => {
				renders.current = 0;
				if (inputRef.current) syncHeightWithFlushSycn();
			});
			let resizeObserver;
			const input_0 = inputRef.current;
			const containerWindow_0 = ownerWindow(input_0);
			containerWindow_0.addEventListener("resize", handleResize);
			if (typeof ResizeObserver !== "undefined") {
				resizeObserver = new ResizeObserver(handleResize);
				resizeObserver.observe(input_0);
			}
			return () => {
				handleResize.clear?.();
				containerWindow_0.removeEventListener("resize", handleResize);
				if (resizeObserver) resizeObserver.disconnect();
			};
		};
		$[17] = syncHeightWithFlushSycn;
		$[18] = t6;
	} else t6 = $[18];
	React$1.useEffect(t6);
	let t7;
	if ($[19] !== syncHeight) {
		t7 = () => {
			syncHeight();
		};
		$[19] = syncHeight;
		$[20] = t7;
	} else t7 = $[20];
	useEnhancedEffect(t7);
	let t8;
	if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
		t8 = () => {
			renders.current = 0;
		};
		$[21] = t8;
	} else t8 = $[21];
	let t9;
	if ($[22] !== value) {
		t9 = [value];
		$[22] = value;
		$[23] = t9;
	} else t9 = $[23];
	React$1.useEffect(t8, t9);
	let t10;
	if ($[24] !== onChange || $[25] !== syncHeight) {
		t10 = (event) => {
			renders.current = 0;
			if (!isControlled) syncHeight();
			if (onChange) onChange(event);
		};
		$[24] = onChange;
		$[25] = syncHeight;
		$[26] = t10;
	} else t10 = $[26];
	const handleChange = t10;
	const t11 = minRows;
	const t12 = state.overflow ? "hidden" : void 0;
	let t13;
	if ($[27] !== state.outerHeightStyle || $[28] !== style || $[29] !== t12) {
		t13 = {
			height: state.outerHeightStyle,
			overflow: t12,
			...style
		};
		$[27] = state.outerHeightStyle;
		$[28] = style;
		$[29] = t12;
		$[30] = t13;
	} else t13 = $[30];
	let t14;
	if ($[31] !== handleChange || $[32] !== handleRef || $[33] !== other || $[34] !== t11 || $[35] !== t13 || $[36] !== value) {
		t14 = /* @__PURE__ */ jsx("textarea", {
			value,
			onChange: handleChange,
			ref: handleRef,
			rows: t11,
			style: t13,
			...other
		});
		$[31] = handleChange;
		$[32] = handleRef;
		$[33] = other;
		$[34] = t11;
		$[35] = t13;
		$[36] = value;
		$[37] = t14;
	} else t14 = $[37];
	let t15;
	if ($[38] !== style) {
		t15 = {
			...styles.shadow,
			...style,
			paddingTop: 0,
			paddingBottom: 0
		};
		$[38] = style;
		$[39] = t15;
	} else t15 = $[39];
	let t16;
	if ($[40] !== props.className || $[41] !== t15) {
		t16 = /* @__PURE__ */ jsx("textarea", {
			"aria-hidden": true,
			className: props.className,
			readOnly: true,
			ref: shadowRef,
			tabIndex: -1,
			style: t15
		});
		$[40] = props.className;
		$[41] = t15;
		$[42] = t16;
	} else t16 = $[42];
	let t17;
	if ($[43] !== t14 || $[44] !== t16) {
		t17 = /* @__PURE__ */ jsxs(React$1.Fragment, { children: [t14, t16] });
		$[43] = t14;
		$[44] = t16;
		$[45] = t17;
	} else t17 = $[45];
	return t17;
});
var TextareaAutosize_default = TextareaAutosize;

//#endregion
export { TextareaAutosize_default as default };
//# sourceMappingURL=TextareaAutosize.js.map